import * as React from 'react';
import * as classnames from 'classnames';
import Select from 'react-select';
import { QTYPES, Question } from '@nexxt/common/types';

interface VariableFunctionEditorProps {
    showError: boolean;
    errorObject: any;
    touched: any;
    disabled: boolean;
    selectedVariableFunction: any;
    variableFunctions: any[];
    isLoading: boolean;
    handleVariableFunctionSelect: (operator) => void;
}

/**
 * This component provides a UI for researcher to define a logical function which
 * is evaluated upon a particular IncaVariable.
 */
const VariableFunctionEditor: React.FC<VariableFunctionEditorProps> = ({
    showError,
    errorObject,
    touched,
    disabled,
    selectedVariableFunction,
    variableFunctions,
    isLoading,
    handleVariableFunctionSelect
}: VariableFunctionEditorProps) => {
    return (
        <>
            <span
                className={classnames('condition--rule--title', {
                    error:
                        (showError || touched.selectedOperator) &&
                        errorObject?.selectedOperator
                })}
            >
                Condition
            </span>
            <Select
                className="react-select"
                classNamePrefix={`${
                    (showError || touched.selectedOperator) &&
                    errorObject?.selectedOperator
                        ? 'error'
                        : ''
                } react-select-condition`}
                placeholder="Select Condition"
                options={variableFunctions}
                onChange={handleVariableFunctionSelect}
                value={selectedVariableFunction}
                isDisabled={disabled || isLoading}
                isLoading={isLoading}
            />
        </>
    );
};

interface VariableOptionEditorProps {
    showError: boolean;
    errorObject: any;
    selectedVariableFunction: any;
    rule: any;
    disabled: boolean;
    options: any[];
    targets: any[];
    touched: any;
    isLoading: boolean;
    selectedQuestion?: Question | null;
    handleOptionSelect: (
        type,
        option,
        compareType?: 'REFERENCE_OPTION' | 'CUSTOM_VALUE' | 'REFERENCE_TARGET'
    ) => void;
}

const VariableOptionEditor: React.FC<VariableOptionEditorProps> = ({
    showError,
    errorObject,
    selectedVariableFunction,
    rule,
    disabled,
    options,
    targets,
    touched,
    isLoading,
    selectedQuestion,
    handleOptionSelect
}: VariableOptionEditorProps) => {
    const footer = () => {
        switch (selectedQuestion?.type) {
            case QTYPES.TRADE_OFF:
                return <span className="text-right">in the top choice</span>;
            default:
                return null;
        }
    };
    switch (selectedVariableFunction?.args) {
        case 'options':
            return (
                <>
                    <span
                        className={classnames('condition--rule--title', {
                            error:
                                (showError || touched.value) &&
                                errorObject?.value
                        })}
                    >
                        Choice
                    </span>
                    <Select
                        className="react-select"
                        classNamePrefix={`${
                            (showError || touched.value) && errorObject?.value
                                ? 'error'
                                : ''
                        } react-select-condition`}
                        placeholder="Select Choice"
                        isMulti
                        options={options}
                        onChange={(opt) =>
                            handleOptionSelect(
                                'options',
                                opt,
                                'REFERENCE_OPTION'
                            )
                        }
                        getOptionValue={(option) => option.optionKey}
                        value={rule?.compareOptions}
                        isDisabled={disabled || isLoading}
                        isLoading={isLoading}
                    />
                    {footer()}
                </>
            );
        case 'targets':
            return (
                <>
                    <span
                        className={classnames('condition--rule--title', {
                            error:
                                (showError || touched.value) &&
                                errorObject?.value
                        })}
                    >
                        Choice
                    </span>
                    <Select
                        className="react-select"
                        classNamePrefix={`${
                            (showError || touched.value) && errorObject?.value
                                ? 'error'
                                : ''
                        } react-select-condition`}
                        placeholder="Select Choice"
                        isMulti
                        options={targets}
                        onChange={(opt) =>
                            handleOptionSelect(
                                'options',
                                opt,
                                'REFERENCE_TARGET'
                            )
                        }
                        getOptionValue={(target) => target.id}
                        value={rule?.compareOptions}
                        isDisabled={disabled || isLoading}
                        isLoading={isLoading}
                    />
                    {footer()}
                </>
            );
        case 'number':
            return (
                <>
                    <span
                        className={classnames('condition--rule--title', {
                            error:
                                (showError || touched.value) &&
                                errorObject?.value
                        })}
                    >
                        Custom Value
                    </span>
                    <input
                        className="form-control"
                        type="number"
                        value={rule.compareNumber}
                        onChange={(event) =>
                            handleOptionSelect(
                                'number',
                                event.target.value,
                                'CUSTOM_VALUE'
                            )
                        }
                    />
                </>
            );
        case 'string':
            return (
                <>
                    <span
                        className={classnames('condition--rule--title', {
                            error:
                                (showError || touched.value) &&
                                errorObject?.value
                        })}
                    >
                        Custom Value
                    </span>
                    <input
                        className="form-control"
                        type="text"
                        value={rule.compareCustomValue?.value}
                        onChange={(event) =>
                            handleOptionSelect(
                                'string',
                                {
                                    ...rule?.compareCustomValue,
                                    value: event.target.value
                                },
                                'CUSTOM_VALUE'
                            )
                        }
                    />
                </>
            );
        case 'rank':
            return (
                <>
                    <span
                        className={classnames('condition--rule--title', {
                            error:
                                (showError || touched.value) &&
                                errorObject?.value
                        })}
                    >
                        In a specific RANK Order
                    </span>
                    <Select
                        className="react-select"
                        classNamePrefix={`${
                            (showError || touched.value) && errorObject?.value
                                ? 'error'
                                : ''
                        } react-select-condition`}
                        placeholder="Select Rank Order"
                        options={options}
                        onChange={(opt) =>
                            handleOptionSelect(
                                'string',
                                opt,
                                'REFERENCE_OPTION'
                            )
                        }
                        getOptionValue={(option) => option.value}
                        value={rule?.compareCustomValue}
                        isDisabled={disabled || isLoading}
                        isLoading={isLoading}
                    />
                </>
            );
    }

    return null;
};

export { VariableFunctionEditor, VariableOptionEditor };
